import { useTranslation } from 'react-i18next';

import { getPOSStatusTooltip } from '@/modules/backoffice/helpers/get-pos-status-label';

import styles from './step-confirm.module.css';

import type { StepConfirmProps } from './step-confirm.type';
import type { ICreateTerminalResponse } from '@/modules/backoffice/types/terminal';
import type { IConfigureTerminalResponse } from '@/modules/backoffice/types/terminal';
import { OperationResultTable } from '../../../operation-result-table/operation-result-table';

export const StepConfirm = ({
  operationType,
  operationStatus,
  operationResult,
}: StepConfirmProps) => {
  const { t } = useTranslation();

  if (operationStatus === 'loading') {
    return (
      <div className={styles['register-loading']}>
        <div className={styles['register-spinner']} />
        <span className={styles['register-loading-title']}>
          {t('backoffice.terminalsDrawerRegisterProcessingTitle')}
        </span>
        <span className={styles['register-loading-subtitle']}>
          {t('backoffice.terminalsDrawerRegisterProcessingSubtitle')}
        </span>
      </div>
    );
  }

  if (operationStatus === 'error') {
    return (
      <div className={styles['register-error']}>
        <span className={styles['register-error-title']}>
          {t('backoffice.terminalsDrawerRegisterErrorTitle')}
        </span>
        <span className={styles['register-error-subtitle']}>
          {t('backoffice.terminalsDrawerRegisterErrorSubtitle')}
        </span>
      </div>
    );
  }

  if (operationStatus === 'done' && operationResult) {
    const successCount = operationResult.success.length;
    const errorCount = operationResult.errors.length;

    const batchTitleKey =
      operationType === 'REGISTER'
        ? 'backoffice.terminalsDrawerRegisterBatchTitle'
        : operationType === 'LINK'
          ? 'backoffice.terminalsDrawerLinkBatchTitle'
          : 'backoffice.terminalsDrawerUnlinkBatchTitle';

    if (operationType === 'REGISTER') {
      const registerResult = operationResult as ICreateTerminalResponse;
      const allItems = [
        ...registerResult.success.map((item) => ({ ...item, isSuccess: true })),
        ...registerResult.errors.map((item) => ({ ...item, isSuccess: false })),
      ];

      return (
        <div className={styles['register-result']}>
          <p className={styles['register-result-subtitle']}>
            {t('backoffice.terminalsDrawerRegisterResultSubtitle')}
          </p>

          <div className={styles['register-result-header']}>
            <span className={styles['register-result-batch-title']}>
              {t(batchTitleKey)}
            </span>
            <div className={styles['register-result-badges']}>
              {successCount > 0 && (
                <span className={styles['badge-success']}>
                  {t('backoffice.terminalsDrawerRegisterSuccessBadge', { count: successCount })}
                </span>
              )}
              {errorCount > 0 && (
                <span className={styles['badge-error']}>
                  {t('backoffice.terminalsDrawerRegisterErrorBadge', { count: errorCount })}
                </span>
              )}
            </div>
          </div>

          <div className={styles['result-table']}>
            <div className={styles['result-table-header']}>
              <span>{t('backoffice.terminalsDrawerResultColumnSerial')}</span>
              <span>{t('backoffice.terminalsDrawerResultColumnIntermediary')}</span>
              <span>{t('backoffice.terminalsDrawerResultColumnFeedback')}</span>
            </div>
            {allItems.map((item) => (
              <div
                key={item.serialNumber}
                className={[
                  styles['result-table-row'],
                  item.isSuccess ? styles['result-table-row--success'] : styles['result-table-row--error'],
                ].join(' ')}
              >
                <span className={styles['result-serial']}>{item.serialNumber}</span>
                <span className={styles['result-intermediary']}>—</span>
                <span className={styles['feedback-tooltip-wrapper']}>
                  <span className={styles['result-feedback']}>
                    <span className={[
                      styles['feedback-dot'],
                      item.isSuccess ? styles['feedback-dot--success'] : styles['feedback-dot--error'],
                    ].join(' ')} />
                    {item.isSuccess
                      ? t('backoffice.terminalsDrawerResultFeedbackSuccess')
                      : t('backoffice.terminalsDrawerResultFeedbackError')}
                  </span>
                  <span className={styles['feedback-tooltip']}>
                    <span className={styles['feedback-tooltip-title']}>
                      {item.isSuccess
                        ? t('backoffice.terminalsDrawerTooltipSuccessTitle')
                        : t('backoffice.terminalsDrawerTooltipErrorTitle')}
                    </span>
                    <span className={styles['feedback-tooltip-body']}>
                      {getPOSStatusTooltip(item.status ?? '', t)}
                    </span>
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    const configureResult = operationResult as IConfigureTerminalResponse;
    const allConfigureItems = [
      ...configureResult.success.map((item) => ({ ...item, isSuccess: true })),
      ...configureResult.errors.map((item) => ({ ...item, isSuccess: false })),
    ];

    const payfacColumnKey =
      operationType === 'LINK'
        ? 'backoffice.terminalsDrawerResultColumnPayfacAfter'
        : 'backoffice.terminalsDrawerResultColumnPayfacBefore';

    return (
      <div className={styles['register-result']}>
        <p className={styles['register-result-subtitle']}>
          {t('backoffice.terminalsDrawerRegisterResultSubtitle')}
        </p>

        <div className={styles['register-result-header']}>
          <span className={styles['register-result-batch-title']}>
            {t(batchTitleKey)}
          </span>
          <div className={styles['register-result-badges']}>
            {successCount > 0 && (
              <span className={styles['badge-success']}>
                {t('backoffice.terminalsDrawerRegisterSuccessBadge', { count: successCount })}
              </span>
            )}
            {errorCount > 0 && (
              <span className={styles['badge-error']}>
                {t('backoffice.terminalsDrawerRegisterErrorBadge', { count: errorCount })}
              </span>
            )}
          </div>
        </div>

        <OperationResultTable
          items={allConfigureItems.map((item) => {
            const payfacId = operationType === 'LINK' ? item.actualPayfacId : item.previousPayfacId;
            const payfacName = operationType === 'LINK' ? item.actualPayfacName : item.previousPayfacName;
            const sellerId = operationType === 'LINK' ? item.actualSellerId : item.previousSellerId;
            const sellerName = operationType === 'LINK' ? item.actualSellerName : item.previousSellerName;
            return {
              serialNumber: item.serialNumber,
              entityId: payfacId,
              entityName: payfacName,
              subEntityId: sellerId,
              subEntityName: sellerName,
              success: item.isSuccess,
              tooltipTitle: item.isSuccess
                ? t('backoffice.terminalsDrawerTooltipSuccessTitle')
                : t('backoffice.terminalsDrawerTooltipErrorTitle'),
              tooltipBody: getPOSStatusTooltip(item.status ?? '', t),
            };
          })}
          entityColumnLabel={t(payfacColumnKey)}
          subEntityColumnLabel={t('backoffice.terminalsDrawerResultColumnSeller')}
        />
      </div>
    );
  }

  return null;
};
